import { AnalysisPlanProto, AnalysisStepProto, KGObservationProto, ObservationEditType, RecommendationProto } from "build-ts/analysis_plan";
import { FilterProto, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { StepInterface } from "../containers/analysis-plan/Interface";
import { getKGGrowthBenchmarks, mergeMetricEdgesByType } from "utils/analysisUtils";
import { ObservationUtils } from "utils/ObservationUtils";
import { AnalysisColumnType, ColumnType, MetadataTypeEnum } from "build-ts/enums";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { getMetricById } from "services/MetricService";
import { getRecommendationEdits } from "services/AnalysisTreeServices";
import { GetRecommendations_Request } from "build-ts/interface";
import { ColumnHashProto } from "build-ts/data";


// Helper function to bifurcate the benchmark_column_hashes from analysis_columns
export const bifurcateBenchmarkColumnHashesAndAnalysisColumns = 
          (analysisColumns: ColumnHashProto[], metrics?: MetricProto[], benchmarks?: BenchmarkEntityProto[], columns?: ObservationColumnProto[]): 
              {benchmarkColumnHashes: ColumnHashProto[], analysisColumnsHashes: ColumnHashProto[]} => {
  let benchmarkColumnHashes: ColumnHashProto[] = [];
  const analysisColumnsHashes: ColumnHashProto[] = [];
 
  analysisColumns.forEach(hash => {
    if(hash.analysis_column_type === AnalysisColumnType.NOT_ANALYSIS_COLUMN || hash.analysis_column_type === undefined){
      benchmarkColumnHashes.push(hash);
    }else{
      analysisColumnsHashes.push(hash);
    }
  });

  // for old plans, where benchmark_column_hashes is not present
  if(benchmarkColumnHashes.length === 0){
    const newBenchmarkColumnHashes: ColumnHashProto[] = [];
    metrics?.forEach(metric => {
      benchmarks?.forEach(benchmark => {
        newBenchmarkColumnHashes.push(ColumnHashProto.create({
          actuals_id: metric.header?.id,
          benchmark_id: benchmark.header?.id,
        }));
      });
    });
    columns?.filter(col => col.column_type === ColumnType.MEASURE).forEach(col => {
      benchmarks?.forEach(benchmark => {
        newBenchmarkColumnHashes.push(ColumnHashProto.create({
          actuals_id: col.column_id,
          benchmark_id: benchmark.header?.id,
        }));
      });
    });
    benchmarkColumnHashes = newBenchmarkColumnHashes;
  }
  return { benchmarkColumnHashes, analysisColumnsHashes };
};

// New function to convert a plan proto to nodeMap and tree
export const buildNodeMapFromPlan = async (
  plan: AnalysisPlanProto,
  mkgs: MetricProto[]
): Promise<{
  nodeMap: Map<string, StepInterface>;
  rootNode: StepInterface | null;
}> => {
  const nodeMap = new Map<string, StepInterface>();
  let rootNode: StepInterface | null = null;

  // Create a map of all steps by ID for quick lookup
  const stepsById = new Map<string, AnalysisStepProto>();
  if (plan.analysis_steps) {
    plan.analysis_steps.forEach((step) => {
      if (step.id) {
        stepsById.set(step.id, step);
      }
    });
  }

  // Find the root step by ID
  const rootStepId = plan.root_step_id;
  const rootStep = rootStepId ? stepsById.get(rootStepId) : null;

  if (rootStep) {
    // Process metric data using our utility function
    const processedEdges = mergeMetricEdgesByType(mkgs);

    // Convert headers to ObservationColumnProto objects
    const attributeColumns =
      ObservationUtils.getObservationColumns(processedEdges.attributeHeaders) ||
      [];
    const measureColumns =
      ObservationUtils.getObservationColumns(processedEdges.measureHeaders) ||
      [];
    const temporalColumns =
      ObservationUtils.getObservationColumns(processedEdges.temporalHeaders) ||
      [];
    const metricColumns =
      ObservationUtils.getObservationColumns(
        processedEdges?.metricEdgeHeaders || []
      ) || [];

    // Extract metrics from metric columns
    const availableMetrics: MetricProto[] = [];
    metricColumns.forEach((col) => {
      if (col.kg_column_header?.type === MetadataTypeEnum.METRIC) {
        // Create a MetricProto from the column header
        const metric = MetricProto.create({
          header: col.kg_column_header,
        });
        availableMetrics.push(metric);
      }
    });

    // Don't include metric columns in non_benchmarks - they should be handled separately
    const nonBenchmarks = [
      ...attributeColumns,
      ...measureColumns,
      ...temporalColumns,
    ];

    const selectedBenchmarks =
      plan.root_observation?.benchmarks?.filter(Boolean) || [];
    const selectedColumns =
      plan.root_observation?.columns?.filter(Boolean) || [];
    const selectedFilters =
      plan.root_observation?.filters?.filter(Boolean) || [];

    // All growth(Versus) benchmarks.
    const growthBenchmarks = getKGGrowthBenchmarks(selectedFilters);
    const allBenchmarks = [
      ...processedEdges.benchmarkEntities,
      ...growthBenchmarks,
    ];

    // Create metrics array - use all metrics from the array (selected metrics)
    let selectedMetrics: MetricProto[] = [...mkgs];

    // Also include any metrics from the root observation
    if (plan.root_observation?.metrics) {
      // Filter out duplicates
      const existingMetricIds = new Set(
        selectedMetrics.map((m) => m.header?.id)
      );
      plan.root_observation.metrics.forEach((m) => {
        if (m.header?.id && !existingMetricIds.has(m.header.id)) {
          selectedMetrics.push(m);
          existingMetricIds.add(m.header.id);
        }
      });
    }

    // Combine available metrics: selected metrics + any additional metrics from edges
    const allAvailableMetrics = [...selectedMetrics];
    availableMetrics.forEach((metric) => {
      // Add if not already present
      if (
        !allAvailableMetrics.some(
          (existing) => existing.header?.id === metric.header?.id
        )
      ) {
        allAvailableMetrics.push(metric);
      }
    });

    // Create recommendation_edits_map (empty by default - will be populated later)
    const recommendationEditsMap = new Map<number, RecommendationProto[]>();

    const {benchmarkColumnHashes, analysisColumnsHashes} = bifurcateBenchmarkColumnHashesAndAnalysisColumns(rootStep.analysis_columns || [], selectedMetrics, selectedBenchmarks, selectedColumns);
    rootNode = {
      id: rootStep.id || String(Date.now()),
      label: rootStep.step_label || "Root Plan",
      type: "metric",
      metric_name: plan.header?.name || "",
      // All available metrics that can be selected
      metrics: [...allAvailableMetrics],
      // Currently selected metrics
      selected_metrics: [...selectedMetrics],
      // Reference to available edges - create copies to prevent shared references
      benchmarks: [...allBenchmarks],
      non_benchmarks: [...nonBenchmarks],
      temporal_columns: [...temporalColumns],

      // Selected edges from the plan - create copies to prevent shared references
      selected_benchmarks: [...selectedBenchmarks],
      selected_columns: [...selectedColumns],
      selected_filters: [...selectedFilters],

      
      // Use the analysis_columns from the root step - create copy
      analysis_columns: [...(analysisColumnsHashes)],

      // Set benchmark_column_hashes from root_observation.benchmark_columns
      benchmark_column_hashes: [...(benchmarkColumnHashes)],

      // Set recommendations - create copy
      recommendation_rules: [...(rootStep.recommendation_rules || [])],

      // Initialize recommendation children map
      recommendation_children_map: new Map(),

      // Initialize recommendation edits map
      recommendation_edits_map: recommendationEditsMap,

      // Initialize recommendation edits array
      recommendation_edits: [],

      // Create KG observation - create a deep copy if it exists
      kg_observation: plan.root_observation
        ? KGObservationProto.create({
            metrics: [...(plan.root_observation.metrics || [])],
            benchmarks: [...(plan.root_observation.benchmarks || [])],
            filters: [...(plan.root_observation.filters || [])],
            columns: [...(plan.root_observation.columns || [])],
          })
        : null,
    };

    // Add to nodeMap
    nodeMap.set(rootNode.id, rootNode);

    // Process recommendation rules and child nodes recursively
    if (rootNode && rootNode.recommendation_rules?.length > 0) {
      for (
        let index = 0;
        index < rootNode.recommendation_rules.length;
        index++
      ) {
        const rule = rootNode.recommendation_rules[index];
        if (!rootNode) continue; // Additional null check for TypeScript

        // Process child steps attached to this recommendation
        const childIds: string[] = rule.child_step_ids || [];

        // Update root node's recommendation_children_map
        if (childIds.length > 0) {
          if (!rootNode.recommendation_children_map) {
            rootNode.recommendation_children_map = new Map();
          }
          rootNode.recommendation_children_map.set(index, childIds);

          // Process each child step
          for (const childId of childIds) {
            const childStep = stepsById.get(childId);
            if (childStep && rootNode) {
              // Process this child and all its descendants recursively
              await processChildNode(
                childStep,
                rootNode,
                index,
                nodeMap,
                stepsById
              );
            }
          }
        }
      }
    }
  }

  return { nodeMap, rootNode };
};

// Helper function to process child nodes recursively
const processChildNode = async (
  childStep: AnalysisStepProto,
  parentNode: StepInterface,
  recommendationIndex: number,
  nodeMap: Map<string, StepInterface>,
  stepsById: Map<string, AnalysisStepProto>
) => {
  if (!childStep.id) return; // Skip if no ID

  // Check if we've already processed this node
  if (nodeMap.has(childStep.id)) {
    return;
  }

  // Extract observation edits by type
  const addColumns: ObservationColumnProto[] = [];
  const removeColumns: ObservationColumnProto[] = [];

  const addMetrics: MetricProto[] = [];
  const removeMetrics: MetricProto[] = [];

  const addFilters: FilterProto[] = [];
  const removeFilters: FilterProto[] = [];

  const addBenchmarks: BenchmarkEntityProto[] = [];
  const removeBenchmarks: BenchmarkEntityProto[] = [];

  // Process each observation edit
  childStep.observation_edits?.forEach((edit) => {
    switch (edit.edit_type) {
      case ObservationEditType.ADD_COLUMN:
        if (edit.column) addColumns.push(edit.column);
        break;
      case ObservationEditType.REMOVE_COLUMN:
        if (edit.column) removeColumns.push(edit.column);
        break;
      case ObservationEditType.ADD_FILTER:
        if (edit.filter) addFilters.push(edit.filter);
        break;
      case ObservationEditType.REMOVE_FILTER:
        if (edit.filter) removeFilters.push(edit.filter);
        break;
      case ObservationEditType.ADD_BENCHMARK:
        if (edit.benchmark) addBenchmarks.push(edit.benchmark);
        break;
      case ObservationEditType.REMOVE_BENCHMARK:
        if (edit.benchmark) removeBenchmarks.push(edit.benchmark);
        break;
      case ObservationEditType.ADD_METRIC:
        if (edit.metric) addMetrics.push(edit.metric);
        break;
      case ObservationEditType.REMOVE_METRIC:
        if (edit.metric) removeMetrics.push(edit.metric);
        break;
    }
  });

  // Reconstruct selected items based on parent
  // In a child node, we start with parent's selections and apply the edits
  let selectedColumns = [...(parentNode.selected_columns || [])];
  let childSelectedMetrics = [...(parentNode.selected_metrics || [])];
  let selectedBenchmarks = [...(parentNode.selected_benchmarks || [])];
  let selectedFilters = [...(parentNode.selected_filters || [])];

  // Apply column edits
  if (addColumns.length) {
    const addColumnsObjects = addColumns.map((col) => {
      // Ensure column has proper kg_column_header
      if (!col.kg_column_header && col.logical_column_header) {
        return ObservationColumnProto.create({
          column_id: col.column_id || "",
          kg_column_header: col.logical_column_header, // Use logical header as fallback
          logical_column_header: col.logical_column_header,
        });
      }
      return col;
    });
    selectedColumns = [...selectedColumns, ...addColumnsObjects];
  }
  if (removeColumns.length) {
    const removeColumnIds = new Set(
      removeColumns
        .map((col) => col?.column_id || col?.kg_column_header?.id || "")
        .filter((id) => id !== "")
    );
    selectedColumns = selectedColumns?.filter(
      (col) => !removeColumnIds.has(col?.column_id || "")
    );
  }

  // Apply metric edits
  if (addMetrics.length) {
    childSelectedMetrics = [...childSelectedMetrics, ...addMetrics];
  }
  if (removeMetrics.length) {
    const removeMetricIds = new Set(removeMetrics.map((m) => m.header?.id));
    childSelectedMetrics = childSelectedMetrics?.filter(
      (m) => !removeMetricIds.has(m.header?.id)
    );
  }

  // Ensure all child metrics have edges loaded (similar to handleAddChildNode)
  childSelectedMetrics = await Promise.all(
    childSelectedMetrics?.map(async (metric) => {
      if (!metric.edges || metric.edges.length === 0) {
        const updatedMetric = await getMetricById(
          metric.header?.id!,
          true /* include edges */
        );
        return updatedMetric || metric;
      }
      return metric;
    })
  );

  // Apply benchmark edits
  if (addBenchmarks.length) {
    selectedBenchmarks = [...selectedBenchmarks, ...addBenchmarks];
  }
  if (removeBenchmarks.length) {
    const removeBenchmarkIds = new Set(
      removeBenchmarks.map((b) => b.header?.id)
    );
    selectedBenchmarks = selectedBenchmarks?.filter(
      (b) => !removeBenchmarkIds.has(b.header?.id)
    );
  }

  // Apply filter edits
  if (addFilters.length) {
    selectedFilters = [...selectedFilters, ...addFilters];
  }
  if (removeFilters.length) {
    // Filters might have filter_id not id
    const removeFilterIds = new Set(
      removeFilters.map((f) => f.filter_id || "")
    );
    selectedFilters = selectedFilters?.filter(
      (f) => !removeFilterIds.has(f.filter_id || "")
    );
  }

  // Calculate available options based on selected metrics
  const processedEdges = mergeMetricEdgesByType(childSelectedMetrics);

  // Get columns directly from processed edges
  const attributeColumns =
    ObservationUtils.getObservationColumns(processedEdges?.attributeHeaders) ||
    [];
  const measureColumns =
    ObservationUtils.getObservationColumns(processedEdges?.measureHeaders) ||
    [];
  const temporalColumns =
    ObservationUtils.getObservationColumns(processedEdges?.temporalHeaders) ||
    [];

  // Create available metrics: include the selected metrics (with full data) plus any additional metrics from processed edges
  const availableMetrics: MetricProto[] = [...childSelectedMetrics];

  // Add any additional metrics from processed edge headers that aren't already in selected metrics
  processedEdges?.metricEdgeHeaders?.forEach((header) => {
    // Check if this metric is already in availableMetrics
    if (
      !availableMetrics.some((existing) => existing.header?.id === header.id)
    ) {
      availableMetrics.push(MetricProto.create({ header: header }));
    }
  });

  // Get benchmarks from processed edges and add growth benchmarks
  const availableBenchmarks = [...(processedEdges?.benchmarkEntities || [])];
  // Add growth benchmarks from the growth benchmark utility
  const growthBenchmarks = getKGGrowthBenchmarks(selectedFilters);
  availableBenchmarks.push(...growthBenchmarks);

  // Combine all available columns for non_benchmarks
  const allAvailableColumns = [
    ...attributeColumns,
    ...measureColumns,
    ...temporalColumns,
  ];

  // Create a KG observation for this node.
  const kgObservation = KGObservationProto.create({
    metrics: [...childSelectedMetrics],
    benchmarks: selectedBenchmarks,
    filters: selectedFilters,
    columns: selectedColumns,
  });

  // Get recommendation edits from parent for this child - just get from parent's map
  const parentRecommendationEdits =
    parentNode.recommendation_edits_map?.get(recommendationIndex) || [];

  const {benchmarkColumnHashes, analysisColumnsHashes} = bifurcateBenchmarkColumnHashesAndAnalysisColumns(childStep.analysis_columns || [], childSelectedMetrics, selectedBenchmarks, selectedColumns);
  // Create a node for this child
  const childNode: StepInterface = {
    id: childStep.id,
    label: childStep?.step_label || "Child Step",
    type: "transform",

    // Use available metrics calculated from processed edges
    metrics: [...availableMetrics],
    // Use the selected metrics (may have been modified by edits)
    selected_metrics: [...childSelectedMetrics],
    metric_name: parentNode.metric_name,

    // Use calculated available options based on processed edges
    benchmarks: [...availableBenchmarks],
    non_benchmarks: [...allAvailableColumns],
    temporal_columns: [...temporalColumns],

    // Set parent node ID for derivation
    parent_node_id: parentNode.id,

    // Set selected items (keep as they are from observation edits)
    selected_columns: selectedColumns,
    selected_benchmarks: selectedBenchmarks,
    selected_filters: selectedFilters,

    // Use analysis_columns from the step
    analysis_columns: analysisColumnsHashes,

    // Set benchmark_column_hashes
    benchmark_column_hashes: benchmarkColumnHashes,

    // Set recommendations
    recommendation_rules: childStep.recommendation_rules || [],

    // Initialize recommendation children map
    recommendation_children_map: new Map(),

    // Initialize recommendation edits map for this child
    recommendation_edits_map: new Map(),

    // Store the accumulated recommendation edits from parent
    recommendation_edits: [
      ...(parentNode.recommendation_edits || []),
      ...parentRecommendationEdits,
    ],

    // Set KG observation
    kg_observation: kgObservation,
  };

  // Add to nodeMap
  nodeMap?.set(childNode.id, childNode);

  // Process child's recommendation rules recursively
  if (childNode?.recommendation_rules?.length > 0) {
    for (
      let index = 0;
      index < childNode.recommendation_rules.length;
      index++
    ) {
      const rule = childNode.recommendation_rules[index];
      const childIds: string[] = rule.child_step_ids || [];

      // Update child node's recommendation_children_map
      if (childIds.length > 0) {
        if (!childNode?.recommendation_children_map) {
          childNode.recommendation_children_map = new Map();
        }
        childNode?.recommendation_children_map?.set(index, childIds);

        // Process each child step recursively
        for (const childId of childIds) {
          const nextChildStep = stepsById.get(childId);
          if (nextChildStep) {
            await processChildNode(
              nextChildStep,
              childNode,
              index,
              nodeMap,
              stepsById
            );
          }
        }
      }
    }
  }
};

// Helper function to process recommendation edits for a node
const processNodeRecommendationEdits = async (
  node: StepInterface,
  nodeMap: Map<string, StepInterface>
): Promise<void> => {
  // Initialize recommendation_edits_map if it doesn't exist
  if (!node.recommendation_edits_map) {
    node.recommendation_edits_map = new Map();
  }

  // Create KG observation for this node
  const kgObservation = KGObservationProto.create({
    metrics: node.metrics || [],
    benchmarks: node.selected_benchmarks || [],
    filters: node.selected_filters || [],
    columns:
      node.selected_columns?.filter(
        (col) => col.kg_column_header?.type !== MetadataTypeEnum.METRIC
      ) || [],
  });

  // Process each recommendation rule
  for (let index = 0; index < node.recommendation_rules.length; index++) {
    // If the recommendation edits are already processed, skip.
    const edits = node?.recommendation_edits_map?.get(index);
    if (edits && edits.length > 0) continue;

    const rule = node.recommendation_rules[index];

    if (rule?.selections && rule.selections.length > 0) {
      // Get recommendation edits from API
      const recommendationEdits = await getRecommendationEdits(
        GetRecommendations_Request.create({
          kg_observation: kgObservation,
          selection_criteria: rule.selections[0],
        })
      );

      if (recommendationEdits && recommendationEdits?.recommendations) {
        // Store the recommendation edits in the map
        node.recommendation_edits_map.set(index, recommendationEdits.recommendations);
      } else {
        // Store empty array as fallback
        node.recommendation_edits_map.set(index, []);
      }
    } else {
      // Store empty array if no selection criteria
      node.recommendation_edits_map.set(index, []);
    }
  }

  // Accumulate inherited recommendation edits for UI display
  let accumulatedEdits: RecommendationProto[] = [];
  // Get parent's accumulated edits (if this node has a parent)
  if (node.parent_node_id) {
    const parentNode = nodeMap.get(node.parent_node_id);
    if (parentNode) {
      accumulatedEdits = [...(parentNode.recommendation_edits || [])];
      // Find which recommendation of the parent created this child
      if (parentNode.recommendation_children_map) {
        for (const [recommendationIndex, childIds] of Array.from(parentNode.recommendation_children_map.entries())) {
          if (childIds.includes(node.id)) {
            // Add the specific recommendation edits that created this child
            const parentRecommendationEdits = parentNode.recommendation_edits_map?.get(recommendationIndex) || [];
            accumulatedEdits = [...accumulatedEdits, ...parentRecommendationEdits];
            break;
          }
        }
      }
    }
  }
  
  // Set the accumulated edits for UI display
  node.recommendation_edits = accumulatedEdits;
  nodeMap.set(node.id, node);
};

// Function to process recommendations when a node is clicked.
export const populateRecommendationEdits = async (
  targetNodeId: string,
  nodeMap: Map<string, StepInterface>,
): Promise<void> => {
  // 1. Process path from root to target
  let currentNode = nodeMap.get(targetNodeId);
  if (currentNode?.recommendation_edits?.length 
    && currentNode.recommendation_edits.length > 0) return;
  const pathNodes: StepInterface[] = [];
  
  // Build path to root
  while (currentNode) {
    pathNodes.unshift(currentNode);
    if (!currentNode.parent_node_id) break;
    currentNode = nodeMap.get(currentNode.parent_node_id);
  }
  // 2. Process each node in path - inheritance is handled within processNodeRecommendationEdits
  for (const node of pathNodes) {
    await processNodeRecommendationEdits(node, nodeMap);  
  }
};
